// The single definition of "today's plan" — the ordered set of tasks you're working
// today. The plan ROLLS: anything open that was scheduled for today or any earlier day is in
// it automatically (nothing has to be re-scheduled because it didn't get done), and so is the
// open backlog of any project you've blocked time for today. Everything else — backlog in
// projects you're not on today, tasks dated in the future — lives in a separate "More"
// bucket (selectDayExtras) behind a tab, so mobile stays one-thing-at-a-time.
// Pure — reused by Home (the play button + count) and the Plan card deck so they never
// disagree about what "the plan" is.

import { computeTaskIndices } from './indexing.js';
import { resolveDayOrder } from './dayorder.js';
import { idleProjectIds } from './projectstatus.js';

// Shared plumbing: build the block map + per-project index map + done-set, then rank a set
// of candidate tasks with resolveDayOrder. Both selectors run candidates through this.
function rankCandidates(candidates, { tasks, blocks, sections, today, outcomeTaskIds }) {
  const blocksByProject = new Map();
  blocks.forEach((b) => { if (b.projectId && !blocksByProject.has(b.projectId)) blocksByProject.set(b.projectId, b.startHour); });

  const indices = new Map();
  [...new Set(candidates.map((t) => t.projectId).filter(Boolean))].forEach((pid) => {
    const projSecs = sections.filter((s) => s.projectId === pid);
    computeTaskIndices(tasks.filter((t) => t.projectId === pid), projSecs).forEach((v, k) => indices.set(k, v));
  });

  const doneIds = new Set(tasks.filter((t) => t.status === 'done').map((t) => t.id));
  return resolveDayOrder(candidates, { today, blocksByProject, indices, doneIds, outcomeTaskIds });
}

// Setup / cleanup checklist items (bucket tasks) run from the project screen, never the plan.
// Tasks of a project that isn't active (on hold, completed, cancelled, archived) stay out too — pass
// `projects` for that; without it every project counts as active.
// A routine run's steps (runId) are worked in the routine's own mode, never the plan.
const openRoots = (tasks, idle = null) => tasks.filter((t) => !t.parentId && t.status === 'todo' && !t.bucket && !t.runId && !(idle && t.projectId && idle.has(t.projectId)));
const idleOf = (projects) => (projects ? idleProjectIds(projects) : null);

// Which projects are "on" today: the ones you blocked time for; with no blocks set, the projects
// of whatever is dated today or carried over — so the active job's whole outline is in play without
// having to date every step.
function projectsOnToday(tasks, blocks, today, idle = null) {
  const pids = new Set(blocks.map((b) => b.projectId).filter(Boolean));
  if (pids.size === 0) openRoots(tasks, idle).forEach((t) => { if (t.projectId && t.scheduledDate && t.scheduledDate <= today) pids.add(t.projectId); });
  return pids;
}

/** In the rolling plan: dated today or earlier, or undated in a project that's on today. */
function inPlan(t, today, blockedPids) {
  if (t.scheduledDate) return t.scheduledDate <= today;   // dated later = deliberately not today
  return !!(t.projectId && blockedPids.has(t.projectId));
}

/**
 * The rolling plan: open root tasks dated today or earlier (carried over automatically) plus
 * the open backlog of projects blocked today.
 * @param {Object} args
 * @param {Array}  args.tasks    all tasks
 * @param {Array}  args.blocks   today's time-blocks (already date-filtered)
 * @param {Array}  args.sections all sections (for phase/index ordering)
 * @param {string} args.today    ISO YYYY-MM-DD
 * @param {Array}  [args.projects] all projects — tasks of ones not active are left out
 * @returns {Array} ordered open root tasks that make up today's plan
 */
export function selectDayPlan({ tasks = [], blocks = [], sections = [], today, outcomeTaskIds = null, projects = null }) {
  const idle = idleOf(projects);
  const blockedPids = projectsOnToday(tasks, blocks, today, idle);
  const candidates = openRoots(tasks, idle).filter((t) => inPlan(t, today, blockedPids));
  return rankCandidates(candidates, { tasks, blocks, sections, today, outcomeTaskIds });
}

/**
 * The "More" bucket: everything open that the rolling plan didn't pull in — backlog of
 * projects you're not on today, and tasks dated in the future. Same ranking. Behind a tab.
 * @returns {Array} ordered open root tasks outside today's plan
 */
export function selectDayExtras({ tasks = [], blocks = [], sections = [], today, projects = null }) {
  const idle = idleOf(projects);
  const blockedPids = projectsOnToday(tasks, blocks, today, idle);
  const candidates = openRoots(tasks, idle).filter((t) => !inPlan(t, today, blockedPids));
  return rankCandidates(candidates, { tasks, blocks, sections, today });
}

/**
 * Plan mode's card deck: the day plan in its own order, across projects. A card is worked inside its
 * project (clock, rate, photos), so a task with no project stays on the Plan list and is counted as loose.
 * @param {Array} plan selectDayPlan's result
 * @returns {{ cards: Array, loose: number }}
 */
export function planDeck(plan = []) {
  const cards = plan.filter((t) => t.projectId);
  return { cards, loose: plan.length - cards.length };
}

/** True when there is a plan to show: blocked time, or anything rolled into today. */
export function hasDayPlan({ tasks = [], blocks = [], today, projects = null } = {}) {
  if (blocks.length > 0) return true;
  return openRoots(tasks, idleOf(projects)).some((t) => t.scheduledDate && t.scheduledDate <= today);
}

/** Days a task has been carried: 0 = dated today / undated, n = rolled over n days. */
export function carriedDays(task, today) {
  if (!task.scheduledDate || task.scheduledDate >= today) return 0;
  const a = new Date(task.scheduledDate + 'T12:00:00'), b = new Date(today + 'T12:00:00');
  return Math.round((b - a) / 86400000);
}
